import {Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {CurrencySymbolMap} from '@/constants/currencies';
import type {
  ProcessorBalanceRow,
  ProcessorBalancesResponse,
} from '@/services/processorService';
import type {AfriexTransactionProcessor} from '@/types';

interface ProcessorBalanceTableProps {
  balances: ProcessorBalancesResponse;
  balanceDenomination?: Record<
    string,
    | 'dollar-denominated-global'
    | 'local-denominated'
    | 'dollar-denominated-local'
  > | null;
}

// The server's string sentinels for a row it could not price (see
// ProcessorBalanceRow). Any other string is treated as a failure, so a new
// sentinel shows up in the failed list rather than disappearing.
const DISABLED = 'disabled';
const NOT_SUPPORTED = 'balance check not supported';

// Failed slots for one processor, plus how many slots it has in total, so the
// summary can tell a processor that is down ("all 16") from one that failed
// on a few currencies ("2 of 5").
type FailedGroup = {
  processor: AfriexTransactionProcessor;
  labels: string[];
  total: number;
};

// Disabled and unsupported processors are expected, standing states with
// nothing to act on, so they get the failed box's shape in neutral grey:
// red stays reserved for the one group that needs attention.
const ProcessorStatusPanel = ({
  label,
  processors,
  dataCy,
}: {
  label: string;
  processors: AfriexTransactionProcessor[];
  dataCy: string;
}) => {
  if (processors.length === 0) {
    return null;
  }
  return (
    <section
      aria-label={label}
      data-cy={dataCy}
      className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800/40"
    >
      <p className="font-semibold text-gray-700 dark:text-gray-200">
        {label} ({processors.length})
      </p>
      <ul className="mt-2 flex flex-wrap gap-2">
        {processors.map((processor) => (
          <li
            key={processor}
            className="rounded bg-white px-2 py-1 uppercase text-gray-600 dark:bg-gray-700/50 dark:text-gray-300"
          >
            {processor}
          </li>
        ))}
      </ul>
    </section>
  );
};

const ProcessorBalanceTable = ({
  balances,
  balanceDenomination,
}: ProcessorBalanceTableProps) => {
  // One sorted list feeds the table and the status panels, so they share an
  // order that does not depend on the server's key order.
  const sortedBalances = Object.entries(balances || {}).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  const groupedRows = sortedBalances
    .map(([processor, detail]) => {
      const validRows = (detail?.rows ?? []).filter(
        (row) => typeof row.balance === 'number',
      );

      return {
        processor: processor as AfriexTransactionProcessor,
        rows: validRows,
      };
    })
    .filter((group) => group.rows.length > 0);

  // Rows without a numeric balance used to be dropped, which made a partner
  // whose balance call failed look the same as one that is not on the page.
  // Failures are counted per row, because a processor can price some
  // currencies and fail on others, then grouped by processor so a bad day
  // (or an environment without sandbox credentials) stays readable.
  const failedGroups: FailedGroup[] = [];
  const disabledProcessors = new Set<AfriexTransactionProcessor>();
  const unsupportedProcessors = new Set<AfriexTransactionProcessor>();
  for (const [processor, detail] of sortedBalances) {
    const rows = detail?.rows ?? [];
    const failedLabels: string[] = [];
    for (const row of rows) {
      if (typeof row.balance === 'number') {
        continue;
      }
      const name = processor as AfriexTransactionProcessor;
      if (row.balance === DISABLED) {
        disabledProcessors.add(name);
      } else if (row.balance === NOT_SUPPORTED) {
        unsupportedProcessors.add(name);
      } else {
        failedLabels.push(row.label || row.currency);
      }
    }
    if (failedLabels.length > 0) {
      failedGroups.push({
        processor: processor as AfriexTransactionProcessor,
        labels: failedLabels,
        total: rows.length,
      });
    }
  }
  const failedCount = failedGroups.reduce(
    (sum, group) => sum + group.labels.length,
    0,
  );

  const getFormattedAmount = (
    processor: string,
    currency: string,
    amount: string | number,
  ) => {
    let prefix: string;
    if (balanceDenomination?.[processor] === 'dollar-denominated-local') {
      prefix = '$';
    } else {
      prefix = CurrencySymbolMap[currency] || '';
    }
    const formattedNum = Number(amount).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    });
    return `${prefix}${formattedNum}`;
  };

  // The label is what the server uses to disambiguate slots within a currency
  // (e.g. "USDC · SOLANA_MAINNET" vs "USDC · TRON_MAINNET"). When it's just
  // the currency code we don't repeat it; show the breakdown only when there
  // is one.
  const getLabelSuffix = (row: ProcessorBalanceRow) => {
    if (!row.label || row.label === row.currency) {
      return null;
    }
    return row.label;
  };

  return (
    <div className="flex flex-col gap-4">
      {failedGroups.length > 0 && (
        <section
          aria-label="Failed balance checks"
          data-cy="processor-balance-failed"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm dark:border-red-800 dark:bg-red-900/20"
        >
          <p className="font-semibold text-red-800 dark:text-red-300">
            Could not fetch {failedCount}{' '}
            {failedCount === 1 ? 'balance' : 'balances'} from{' '}
            {failedGroups.length}{' '}
            {failedGroups.length === 1 ? 'processor' : 'processors'}
          </p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {failedGroups.map((group) => (
              <li
                key={group.processor}
                className="flex items-center gap-2 rounded bg-white px-2 py-1 text-red-700 dark:bg-red-900/30 dark:text-red-300"
              >
                <span className="uppercase font-medium">{group.processor}</span>
                <span className="rounded-full bg-red-100 px-1.5 text-xs dark:bg-red-900/50">
                  {group.labels.length === group.total
                    ? `all ${group.total}`
                    : `${group.labels.length} of ${group.total}`}
                </span>
              </li>
            ))}
          </ul>
          <details className="mt-3">
            <summary className="cursor-pointer text-red-700 hover:underline dark:text-red-300">
              Show every slot
            </summary>
            <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-gray-700 dark:text-gray-300">
              {failedGroups.map((group) => (
                <div key={group.processor} className="contents">
                  <dt className="uppercase font-medium">{group.processor}</dt>
                  <dd>{group.labels.join(', ')}</dd>
                </div>
              ))}
            </dl>
          </details>
        </section>
      )}
      <ProcessorStatusPanel
        label="Disabled in this environment, not checked"
        processors={[...disabledProcessors]}
        dataCy="processor-balance-disabled"
      />
      <ProcessorStatusPanel
        label="Balance check not supported"
        processors={[...unsupportedProcessors]}
        dataCy="processor-balance-unsupported"
      />
      <Table>
        <Thead>
          <Th className="pl-10">Processor</Th>
          <Th>Currency</Th>
          <Th>Breakdown</Th>
          <Th>Balance</Th>
        </Thead>
        <TBody>
          {groupedRows.map((group, groupIndex) =>
            group.rows.map((row, idx) => (
              <Tr
                key={`${group.processor}-${row.label}-${row.currency}-${groupIndex}-${idx}`}
              >
                {idx === 0 && (
                  <Td
                    rowSpan={group.rows.length}
                    className="uppercase font-medium text-gray-900 dark:text-gray-100 align-top pt-4 pl-10"
                  >
                    {group.processor}
                  </Td>
                )}
                <Td className="text-gray-600 dark:text-gray-300">
                  {row.currency}
                </Td>
                <Td className="text-gray-500 dark:text-gray-400">
                  {getLabelSuffix(row) ?? '-'}
                </Td>
                <Td className="font-semibold text-gray-800 dark:text-gray-200">
                  {getFormattedAmount(
                    group.processor,
                    row.currency,
                    row.balance,
                  )}
                </Td>
              </Tr>
            )),
          )}
          {groupedRows.length === 0 && (
            <Tr>
              <Td colSpan={4} className="text-center py-6 text-gray-500">
                No balances found
              </Td>
            </Tr>
          )}
        </TBody>
      </Table>
    </div>
  );
};

export default ProcessorBalanceTable;
